import { useEffect, useMemo, useState } from "react";
import { Box, Divider, FormLabel, Input, Typography } from "@mui/joy";
import { Button } from "../../../../../../shared";
import { useProject } from "../../../../../../hooks/query";

export function Customer({ details }: { details: any | null }) {
    const projectId = details?.Id as string | undefined;
    const { useProjectAttributeUpdate } = useProject();
    const updateProjectAttributeSvc = useProjectAttributeUpdate();

    const [name, setName] = useState<string>(details?.CustomerName ?? "");
    const [phone, setPhone] = useState<string>(details?.CustomerPhoneNumber ?? "");
    const [email, setEmail] = useState<string>(details?.CustomerEmail ?? "");
    const [street, setStreet] = useState<string>(details?.CustomerStreet ?? "");
    const [city, setCity] = useState<string>(details?.CustomerCity ?? "");
    const [state, setState] = useState<string>(details?.CustomerState ?? "");
    const [zip, setZip] = useState<string>(details?.CustomerZipCode ?? "");
    const [country, setCountry] = useState<string>(details?.CustomerCountry ?? "");
    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => {
        setName(details?.CustomerName ?? "");
        setPhone(details?.CustomerPhoneNumber ?? "");
        setEmail(details?.CustomerEmail ?? "");
        setStreet(details?.CustomerStreet ?? "");
        setCity(details?.CustomerCity ?? "");
        setState(details?.CustomerState ?? "");
        setZip(details?.CustomerZipCode ?? "");
        setCountry(details?.CustomerCountry ?? "");
    }, [
        details?.CustomerName,
        details?.CustomerPhoneNumber,
        details?.CustomerEmail,
        details?.CustomerStreet,
        details?.CustomerCity,
        details?.CustomerState,
        details?.CustomerZipCode,
        details?.CustomerCountry,
    ]);

    const hasChanges = useMemo(() => {
        return (
            (details?.CustomerName ?? "") !== name ||
            (details?.CustomerPhoneNumber ?? "") !== phone ||
            (details?.CustomerEmail ?? "") !== email ||
            (details?.CustomerStreet ?? "") !== street ||
            (details?.CustomerCity ?? "") !== city ||
            (details?.CustomerState ?? "") !== state ||
            (details?.CustomerZipCode ?? "") !== zip ||
            (details?.CustomerCountry ?? "") !== country
        );
    }, [details, name, phone, email, street, city, state, zip, country]);

    const handleUpdate = async () => {
        if (!projectId || !hasChanges) return;
        setSaving(true);
        try {
            const updates: Array<{ Attribute: string; Value: string }> = [];
            if ((details?.CustomerName ?? "") !== name) updates.push({ Attribute: "CustomerName", Value: name });
            if ((details?.CustomerPhoneNumber ?? "") !== phone)
                updates.push({ Attribute: "CustomerPhoneNumber", Value: phone });
            if ((details?.CustomerEmail ?? "") !== email) updates.push({ Attribute: "CustomerEmail", Value: email });
            if ((details?.CustomerStreet ?? "") !== street)
                updates.push({ Attribute: "CustomerStreet", Value: street });
            if ((details?.CustomerCity ?? "") !== city) updates.push({ Attribute: "CustomerCity", Value: city });
            if ((details?.CustomerState ?? "") !== state) updates.push({ Attribute: "CustomerState", Value: state });
            if ((details?.CustomerZipCode ?? "") !== zip) updates.push({ Attribute: "CustomerZipCode", Value: zip });
            if ((details?.CustomerCountry ?? "") !== country)
                updates.push({ Attribute: "CustomerCountry", Value: country });

            for (const u of updates) {
                await updateProjectAttributeSvc.mutateAsync({ Id: projectId, Attribute: u.Attribute, Value: u.Value });
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ flex: 1 }}>
            <Box mb={2}>
                <Typography level="title-sm">Customer</Typography>
                <Typography level="body-sm">Manage customer contact and address information.</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />

            <Box display={"flex"} flexDirection={"column"} gap={2}>
                <Row label="Customer Name" description="Full name of the customer.">
                    <Input size="sm" value={name} onChange={(e) => setName(e.target.value)} />
                </Row>
                <Row label="Phone" description="Primary contact number.">
                    <Input size="sm" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </Row>
                <Row label="Email" description="Customer email address.">
                    <Input size="sm" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                </Row>
                <Row label="Street" description="Street address.">
                    <Input size="sm" value={street} onChange={(e) => setStreet(e.target.value)} />
                </Row>
                <Row label="City" description="City.">
                    <Input size="sm" value={city} onChange={(e) => setCity(e.target.value)} />
                </Row>
                <Row label="State" description="State/Province.">
                    <Input size="sm" value={state} onChange={(e) => setState(e.target.value)} />
                </Row>
                <Row label="Zip" description="Postal code.">
                    <Input size="sm" value={zip} onChange={(e) => setZip(e.target.value)} />
                </Row>
                <Row label="County" description="County where the customer is located.">
                    <Input size="sm" value={country} onChange={(e) => setCountry(e.target.value)} />
                </Row>

                <Box>
                    <Button
                        onClick={handleUpdate}
                        disabled={!hasChanges || saving}
                        loading={saving}
                        loadingPosition="end"
                    >
                        {saving ? "Updating" : "Update"}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

function Row({ label, description, children }: { label: string; description: string; children: any }) {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "stretch", md: "center" },
                gap: { xs: 0.5, md: 2 },
            }}
        >
            <Box sx={{ flexBasis: { md: 300 }, flexShrink: 0 }}>
                <FormLabel sx={{ m: 0 }}>{label}</FormLabel>
                <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.25 }}>
                    {description}
                </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>{children}</Box>
        </Box>
    );
}
